'use client';
import React, { useState } from 'react';
import StepIndicator from './StepIndicator';
import Step1Services from './Step1Services';
import Step2DateTime from './Step2DateTime';
import Step3ContactForm from './Step3ContactForm';
import BookingConfirmation from './BookingConfirmation';
import BookingSummaryPanel from './BookingSummaryPanel';
import type { BookingState } from './bookingTypes';

const initialState: BookingState = {
  service: null,
  date: null,
  timeSlot: null,
  vehicleType: 'sedan',
  customer: null,
  bookingRef: null,
};

export default function ServiceBookingWizard() {
  const [step, setStep] = useState(1);
  const [booking, setBooking] = useState<BookingState>(initialState);
  const [confirmed, setConfirmed] = useState(false);

  const steps = ['Choose Service', 'Pick Date & Time', 'Your Details'];

  const handleServiceSelect = (service: BookingState['service']) => {
    setBooking((prev) => ({ ...prev, service }));
  };

  const handleDateTimeSelect = (date: string, timeSlot: string) => {
    setBooking((prev) => ({ ...prev, date, timeSlot }));
  };

  const handleVehicleChange = (vehicleType: string) => {
    setBooking((prev) => ({ ...prev, vehicleType }));
  };

  const handleConfirm = (customer: BookingState['customer']) => {
    const ref = `DB-${Math.floor(100000 + Math.random() * 900000)}`;
    setBooking((prev) => ({ ...prev, customer, bookingRef: ref }));
    setConfirmed(true);
  };

  if (confirmed && booking.bookingRef) {
    return <BookingConfirmation booking={booking} onNewBooking={() => { setBooking(initialState); setStep(1); setConfirmed(false); }} />;
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-700 text-foreground">Book Your Detailing Service</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Professional car detailing — choose your service and we'll take care of the rest.
        </p>
      </div>

      <StepIndicator steps={steps} currentStep={step} />

      <div className="mt-8 flex flex-col xl:flex-row gap-8 items-start">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {step === 1 && (
            <Step1Services
              selected={booking.service}
              vehicleType={booking.vehicleType}
              onVehicleChange={handleVehicleChange}
              onSelect={handleServiceSelect}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <Step2DateTime
              selectedDate={booking.date}
              selectedSlot={booking.timeSlot}
              onSelect={handleDateTimeSelect}
              onNext={() => setStep(3)}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <Step3ContactForm
              booking={booking}
              onConfirm={handleConfirm}
              onBack={() => setStep(2)}
            />
          )}
        </div>

        {/* Summary Sidebar */}
        <div className="xl:w-80 w-full flex-shrink-0">
          <BookingSummaryPanel booking={booking} currentStep={step} />
        </div>
      </div>
    </div>
  );
}